/**
 * Client-side rotation through the week's cached Spotlight pool.
 *
 * The pool is fixed for the week (M4S-5315 cache-only — refreshed by the
 * weekly harvest, never by a live call), so "Refresh insights" can't mean
 * "generate more"; it means "show me a different 3". These helpers hold the
 * page math and the localStorage-persisted cursor so a user keeps their
 * place in the rotation across visits within a week.
 */

/** Insights shown per rotation page — the "3 per manual refresh" product rule. */
export const SPOTLIGHT_PAGE_SIZE = 3;

const ROTATION_PREFIX = 'discover-spotlight-rotation:';

export function spotlightPageCount(poolSize: number): number {
    return Math.max(1, Math.ceil(poolSize / SPOTLIGHT_PAGE_SIZE));
}

/** The page's slice of the pool; clamps an out-of-range page to the start. */
export function spotlightPageSlice<T>(pool: T[], page: number): T[] {
    const safePage = page >= 0 && page < spotlightPageCount(pool.length) ? page : 0;
    const start = safePage * SPOTLIGHT_PAGE_SIZE;
    return pool.slice(start, start + SPOTLIGHT_PAGE_SIZE);
}

/** Page containing one insight — used to land `?insight=` deep links. */
export function spotlightPageForInsight<T extends { id: string }>(pool: T[], id: string): number {
    const index = pool.findIndex((insight) => insight.id === id);
    return index >= 0 ? Math.floor(index / SPOTLIGHT_PAGE_SIZE) : 0;
}

interface StoredRotation {
    weekOf: string;
    page: number;
}

/**
 * The persisted cursor for one (userKey, selection, dataset) lane, or 0 when
 * there isn't one, it belongs to an earlier week, or it no longer fits the
 * pool. `rotationKey` is the caller's lane key; `pages` the current pool's
 * page count.
 */
export function readRotationPage(rotationKey: string, weekOf: string, pages: number): number {
    try {
        const raw = window.localStorage.getItem(`${ROTATION_PREFIX}${rotationKey}`);
        if (!raw) return 0;
        const stored = JSON.parse(raw) as StoredRotation;
        if (stored.weekOf !== weekOf) return 0;
        if (!Number.isInteger(stored.page) || stored.page < 0 || stored.page >= pages) return 0;
        return stored.page;
    } catch {
        return 0;
    }
}

export function writeRotationPage(rotationKey: string, weekOf: string, page: number): void {
    try {
        window.localStorage.setItem(
            `${ROTATION_PREFIX}${rotationKey}`,
            JSON.stringify({ weekOf, page } satisfies StoredRotation),
        );
    } catch {
        // Best-effort — worst case the next visit starts at page 0.
    }
}
